// ── Shared chart utilities for Jira Dashboard ────────────────────────────────
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";

export const COLORS = [
    "var(--color-primary)",
    "var(--color-accent)",
    "var(--color-warning)",
    "var(--color-primary-hover)",
    "var(--color-success)",
    "var(--color-error)",
];

// ── Data transforms ───────────────────────────────────────────────────────────

/** Google Charts 2D array → Recharts object array */
export function transformData(rawData) {
    if (!rawData || rawData.length < 2) return { data: [], keys: [], xKey: "" };
    const [headers, ...rows] = rawData;
    const [xKey, ...seriesKeys] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: String(row[0]) };
        seriesKeys.forEach((key, i) => {
            obj[key] = Number(row[i + 1]) || 0;
        });
        return obj;
    });
    return { data, keys: seriesKeys, xKey };
}

/** Simple [range, value] pair array (used by TicketByAge) */
export function transformSimpleData(rawData) {
    if (!rawData || rawData.length < 2)
        return { data: [], xKey: "", valueKey: "" };
    const [headers, ...rows] = rawData;
    const [xKey, valueKey] = headers;
    const data = rows.map((row) => ({
        [xKey]: String(row[0]),
        [valueKey]: Number(row[1]) || 0,
    }));
    return { data, xKey, valueKey };
}

// ── Shared styling ────────────────────────────────────────────────────────────

export const axisStyle = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};

export const gridStyle = {
    strokeDasharray: "3 3",
    stroke: "var(--color-border-light)",
    vertical: false,
};

export const legendFormatter = (v) => (
    <span style={{ color: "var(--color-text-secondary)" }}>{v}</span>
);

export const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                color: "var(--color-text)",
                boxShadow: "var(--shadow-card)",
            }}
        >
            <p
                style={{
                    margin: 0,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "var(--color-text-secondary)",
                    fontSize: 11,
                }}
            >
                {label}
            </p>
            {payload.map((entry) => (
                <p
                    key={entry.name}
                    style={{
                        margin: "2px 0",
                        color: entry.color || entry.fill,
                    }}
                >
                    {entry.name}:{" "}
                    <strong style={{ color: "var(--color-text)" }}>
                        {entry.value}
                    </strong>
                </p>
            ))}
        </div>
    );
};

// ── Chart components ──────────────────────────────────────────────────────────

/** Vertical stacked/grouped bar chart */
export function ColumnChart({ rawData, height = 380, stacked = false }) {
    const { data, keys, xKey } = transformData(rawData);
    if (!data.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                data={data}
                margin={{ top: 10, right: 16, left: 0, bottom: 5 }}
                barCategoryGap="35%"
            >
                <CartesianGrid {...gridStyle} />
                <XAxis dataKey={xKey} {...axisStyle} />
                <YAxis {...axisStyle} allowDecimals={false} />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "var(--color-primary-light)" }}
                />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={legendFormatter}
                />
                {keys.map((key, i) => (
                    <Bar
                        key={key}
                        dataKey={key}
                        fill={COLORS[i % COLORS.length]}
                        radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                        stackId={stacked ? "stack" : undefined}
                        maxBarSize={56}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

/** Horizontal bar chart */
export function HBarChart({ rawData, height = 400, stacked = false }) {
    const { data, keys, xKey } = transformData(rawData);
    if (!data.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 10, right: 16, left: 100, bottom: 5 }}
                barCategoryGap="30%"
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-light)"
                    horizontal={false}
                />
                <XAxis type="number" {...axisStyle} allowDecimals={false} />
                <YAxis
                    type="category"
                    dataKey={xKey}
                    {...axisStyle}
                    width={96}
                />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "var(--color-primary-light)" }}
                />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={legendFormatter}
                />
                {keys.map((key, i) => (
                    <Bar
                        key={key}
                        dataKey={key}
                        fill={COLORS[i % COLORS.length]}
                        radius={stacked ? [0, 0, 0, 0] : [0, 4, 4, 0]}
                        stackId={stacked ? "stack" : undefined}
                        maxBarSize={40}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

/** Simple single-series horizontal bar (for TicketByAge) */
export function SimpleHBarChart({ rawData, height = 360, onBarClick }) {
    const { data, xKey, valueKey } = transformSimpleData(rawData);
    if (!data.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 10, right: 16, left: 100, bottom: 5 }}
                barCategoryGap="30%"
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-light)"
                    horizontal={false}
                />
                <XAxis type="number" {...axisStyle} allowDecimals={false} />
                <YAxis
                    type="category"
                    dataKey={xKey}
                    {...axisStyle}
                    width={96}
                />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "var(--color-primary-light)" }}
                />
                <Bar
                    dataKey={valueKey}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={40}
                    cursor={onBarClick ? "pointer" : "default"}
                    onClick={(entry) => onBarClick && onBarClick(entry)}
                >
                    {data.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

/** Multi-series line chart */
export function MultiLineChart({ rawData, height = 360, hiddenSeries = {} }) {
    const { data, keys, xKey } = transformData(rawData);
    if (!data.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={data}
                margin={{ top: 10, right: 16, left: 0, bottom: 5 }}
            >
                <CartesianGrid {...gridStyle} />
                <XAxis dataKey={xKey} {...axisStyle} />
                <YAxis {...axisStyle} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={legendFormatter}
                />
                {keys.map((key, i) => (
                    <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={COLORS[i % COLORS.length]}
                        strokeWidth={hiddenSeries[i] ? 0 : 2}
                        dot={false}
                        activeDot={{ r: 5 }}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

/** Dual-axis line chart (Tickets + Worklog time) */
export function DualAxisLineChart({
    rawData,
    height = 360,
    hiddenSeries = {},
}) {
    const { data, keys, xKey } = transformData(rawData);
    if (!data.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart
                data={data}
                margin={{ top: 10, right: 40, left: 0, bottom: 5 }}
            >
                <CartesianGrid {...gridStyle} />
                <XAxis dataKey={xKey} {...axisStyle} />
                <YAxis
                    yAxisId="left"
                    {...axisStyle}
                    allowDecimals={false}
                    label={{
                        value: "Tickets",
                        angle: -90,
                        position: "insideLeft",
                        fill: "var(--color-text-muted)",
                        fontSize: 11,
                    }}
                />
                <YAxis
                    yAxisId="right"
                    orientation="right"
                    {...axisStyle}
                    allowDecimals={false}
                    label={{
                        value: "Time",
                        angle: 90,
                        position: "insideRight",
                        fill: "var(--color-text-muted)",
                        fontSize: 11,
                    }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                    formatter={legendFormatter}
                />
                {keys.map((key, i) => (
                    <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        yAxisId={i === 0 ? "left" : "right"}
                        stroke={COLORS[i % COLORS.length]}
                        strokeWidth={hiddenSeries[i] ? 0 : 2}
                        dot={false}
                        activeDot={{ r: 5 }}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

// ── Shared filter bar ─────────────────────────────────────────────────────────
export function FilterBar({ children }) {
    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
                marginBottom: 20,
                padding: "12px 16px",
                background: "var(--color-bg-control)",
                border: "1px solid var(--color-border)",
                borderRadius: 10,
            }}
        >
            {children}
        </div>
    );
}
